// src/durableStore.ts
//
// Durable key/value cache for the offline last-known-good fallbacks.
//
// WHY THIS EXISTS
// ---------------
// On the packaged desktop app the UI loads from file:// (see
// desktop/main.js -> mainWindow.loadFile), where Chromium's localStorage is
// the least reliable store. That is the same reason the Supabase auth session
// and the offline org/role markers already route through
// window.estimatorStore (electron-store — a real JSON file in userData); see
// the header comment in src/supabaseClient.ts.
//
// The July settings hardening ("Layers A/B/C") added a correct last-known-good
// fallback, but parked the cache it falls back to in raw localStorage. On
// desktop that is exactly the store the app elsewhere documents as unreliable,
// so the guard had nothing to fall back TO: a fully-offline launch produced an
// empty cache read, and the proposal rendered with no custom blocks at all.
// Routing those caches through here is what makes the existing guard actually
// hold on desktop.
//
// Web and mobile-web have no window.estimator, so they fall through to
// localStorage and behave exactly as before.

type DesktopStore = {
  get: (key: string) => unknown;
  set: (key: string, val: string) => void;
  remove?: (key: string) => void;
};

// Resolved lazily on every call so we never depend on preload timing and so we
// degrade gracefully if the bridge disappears mid-session.
function desktopStore(): DesktopStore | null {
  if (typeof window === "undefined") return null;
  const w = window as any;
  const isDesktop = w.estimator?.isDesktop === true && !!w.estimatorStore;
  return isDesktop ? (w.estimatorStore as DesktopStore) : null;
}

/**
 * Read a cached value. On desktop this prefers electron-store and performs a
 * one-time read-through migration of any value still sitting in localStorage,
 * so an existing good cache survives the upgrade rather than being discarded.
 */
export function durableGet(key: string): string | null {
  const store = desktopStore();
  if (store) {
    try {
      const fromStore = store.get(key);
      if (fromStore !== null && fromStore !== undefined) {
        return typeof fromStore === "string"
          ? fromStore
          : JSON.stringify(fromStore);
      }
    } catch {
      /* fall through to localStorage */
    }
    // Read-through migration: carry an existing localStorage cache into the
    // durable store so the first launch on this build keeps its blocks.
    try {
      const legacy = window.localStorage.getItem(key);
      if (legacy !== null) {
        try {
          store.set(key, legacy);
        } catch {
          /* best effort — still return the value we found */
        }
        return legacy;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  try {
    return typeof window !== "undefined"
      ? window.localStorage.getItem(key)
      : null;
  } catch {
    return null;
  }
}

/**
 * Write a cached value. Desktop writes go to electron-store AND localStorage:
 * the mirror costs nothing and keeps a downgrade to an older build working.
 */
export function durableSet(key: string, value: string): void {
  const store = desktopStore();
  if (store) {
    try {
      store.set(key, value);
    } catch {
      /* ignore — the localStorage mirror below is the fallback */
    }
  }
  try {
    if (typeof window !== "undefined")
      window.localStorage.setItem(key, value);
  } catch {
    /* quota / unavailable — non-fatal for a cache */
  }
}

export function durableRemove(key: string): void {
  const store = desktopStore();
  if (store) {
    try {
      store.remove?.(key);
    } catch {
      /* ignore */
    }
  }
  try {
    if (typeof window !== "undefined") window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
